import { useEffect } from 'react';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { Button } from 'src/components/ui/button';
import SubscriptionRequired from './subscription-required';

interface ErrorProps {
  error: Error;
  message?: string;
  resource?: string;
  onRetry?: () => void;
  className?: string;
}

export default function Error({ error, message, resource, onRetry, className }: ErrorProps) {
  const router = useRouter();

  // Check if this is a subscription required error
  const isSubscriptionError =
    error.message?.includes('SUBSCRIPTION_REQUIRED') ||
    (error as any)?.graphQLErrors?.some(
      (err: any) => err?.extensions?.code === 'SUBSCRIPTION_REQUIRED'
    );

  // Check if this is a Shopify reapproval required error
  const isReapprovalError =
    error.message?.includes('SHOPIFY_REAPPROVAL_REQUIRED') ||
    error.message?.includes('Shopify permission reapproval required') ||
    (error as any)?.graphQLErrors?.some(
      (err: any) => err?.extensions?.code === 'SHOPIFY_REAPPROVAL_REQUIRED'
    );

  // Redirect to reapproval page if needed
  useEffect(() => {
    if (isReapprovalError) {
      router.replace(paths.shopify.reapproval);
    }
  }, [isReapprovalError, router]);

  if (isSubscriptionError) {
    return <SubscriptionRequired />;
  }

  if (isReapprovalError) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const title = resource ? `Failed to load ${resource}` : 'Something went wrong';
  const description = message || 'An unexpected error occurred. Please try again.';

  return (
    <div className={cn('flex flex-col items-center justify-center py-16', className)}>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <AlertCircle className="size-6 text-muted-foreground" />
      </div>

      <h2 className="text-sm font-medium text-foreground">{title}</h2>
      <p className="mt-1 max-w-xs text-center text-xs text-muted-foreground leading-relaxed">
        {description}
      </p>

      {onRetry && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-5 gap-2"
          onClick={() => onRetry()}
        >
          <RefreshCw className="size-4" />
          Try Again
        </Button>
      )}
    </div>
  );
}
